import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import { UTP2CustomQuestionTypes, UTP2GroupedConditionsLookup, UTP2Groups } from "../../shared/enums/UTP2";
import type {
  CustomQuestionGroup,
  WorkflowMetadataField,
  WorkflowMetadataGroup,
} from "../../shared/interfaces/Workflow";
import type { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import type { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";
import type { ConditionItem } from "./types";

export function createWorkflowMetadataField(
  name: string,
  type: UTP2CustomQuestionTypes,
  value: string,
): WorkflowMetadataField {
  return {
    name,
    type,
    value,
    readonly: false,
    mandatory: null,
    validation: null,
    validation_message: null,
    options: null,
  };
}

export function findSurveyMetadataField(
  workflow: WorkflowV1 | WorkflowV2 | undefined,
  groupName: string,
  fieldName: string,
): WorkflowMetadataField | undefined {
  const group = workflow?.workflowDetail?.survey_metadata_groups?.find((surveyGroup) => surveyGroup.name === groupName);
  const field = group?.fields.find((surveyField) => surveyField.name === fieldName);

  return field ? { ...field } : undefined;
}

export function buildConditionsList(workflow?: WorkflowV1 | WorkflowV2): ConditionItem[] {
  return Object.entries(UTP2GroupedConditionsLookup).map(([group, conditions]) => {
    const checked = conditions.some((condition) =>
      Boolean(findSurveyMetadataField(workflow, UTP2Groups.Conditions, condition)),
    );

    return {
      key: group,
      checked,
    } as ConditionItem;
  });
}

export function findSurveyMetadataCustomQuestionGroups(workflow: WorkflowV1 | WorkflowV2): CustomQuestionGroup[] {
  const definedGroups = Object.values(UTP2Groups);
  const customQuestionGroups = workflow.workflowDetail?.survey_metadata_groups
    ?.filter((group) => !definedGroups.some((definedGroup) => definedGroup === group.name))
    ?.map(
      (group) =>
        ({
          ...group,
          id: uuidv4(),
          fields: group.fields.map((field) => ({ ...field, id: uuidv4() })),
        }) as CustomQuestionGroup,
    );

  return [...(customQuestionGroups || [])];
}

export function updateWorkflowCustomQuestionGroups(
  workflow: WorkflowV1 | WorkflowV2,
  updatedCustomQuestionsGroups: CustomQuestionGroup[],
): WorkflowMetadataGroup[] {
  const definedGroupNames = Object.values(UTP2Groups);
  const definedGroups = workflow.workflowDetail?.survey_metadata_groups?.filter((group) =>
    definedGroupNames.some((name) => name === group.name),
  );

  return [...(definedGroups || []), ...(updatedCustomQuestionsGroups as WorkflowMetadataGroup[])];
}

export function updateWorkflowSurveyMetadataGroups(
  workflow: WorkflowV1 | WorkflowV2,
  surveyMetadataGroups: WorkflowMetadataGroup[],
): WorkflowV1 | WorkflowV2 {
  if (workflow.workflowDetail.version === 1) {
    return {
      ...workflow,
      workflowDetail: {
        ...workflow.workflowDetail,
        survey_metadata_groups: surveyMetadataGroups,
      },
    };
  }

  return {
    ...workflow,
    workflowDetail: {
      ...workflow.workflowDetail,
      survey_metadata_groups: surveyMetadataGroups,
    },
  };
}

export function setWorkflowSurveyMetadataGroupFields(
  workflow: WorkflowV1 | WorkflowV2,
  groupName: string,
  fields: WorkflowMetadataField[],
): WorkflowV1 | WorkflowV2 {
  const updatedWorkflow = cloneDeep(workflow);
  const group = ensureSurveyMetadataGroup(updatedWorkflow, groupName);

  group.fields = [...fields];

  return updatedWorkflow;
}

export function upsertWorkflowSurveyMetadataField(
  workflow: WorkflowV1 | WorkflowV2,
  groupName: string,
  field: WorkflowMetadataField,
): WorkflowV1 | WorkflowV2 {
  const updatedWorkflow = cloneDeep(workflow);
  const group = ensureSurveyMetadataGroup(updatedWorkflow, groupName);
  const existingField = group.fields.find((groupField) => groupField.name === field.name);

  if (existingField) {
    Object.assign(existingField, field);
  } else {
    group.fields.push(field);
  }

  return updatedWorkflow;
}

export function mapConditionsToMetadataFields(conditionItems: ConditionItem[] | undefined): WorkflowMetadataField[] {
  const conditionFields = conditionItems
    ?.filter((conditionItem) => conditionItem.checked)
    .reduce((fieldsAccumulator: WorkflowMetadataField[], conditionItem) => {
      const metadataFields = UTP2GroupedConditionsLookup[conditionItem.key].map((condition) =>
        createWorkflowMetadataField(condition, UTP2CustomQuestionTypes.Native, ""),
      );

      return fieldsAccumulator.concat(metadataFields);
    }, []);

  return conditionFields || [];
}

function ensureSurveyMetadataGroup(workflow: WorkflowV1 | WorkflowV2, groupName: string): WorkflowMetadataGroup {
  let group = workflow.workflowDetail.survey_metadata_groups?.find((surveyGroup) => surveyGroup.name === groupName);

  if (!group) {
    group = {
      name: groupName,
      fields: [],
    };

    workflow.workflowDetail.survey_metadata_groups.push(group);
  }

  return group;
}
